// Wires the map-coverage signal (see ../mapCoverageSignal) onto a Mapbox map.
//
// Deliberately typed against a minimal structural interface rather than
// mapboxgl.Map so the sampling and settle behaviour can be unit tested with a
// plain object — spinning up a real GL map in jsdom is not viable.

import {
  createCoverageReporter,
  CoverageReporter,
  ViewportSample,
} from '../mapCoverageSignal';
import { sendMapCoverageEvent } from '../graphql/map-coverage-graph';

/**
 * The layers that carry HiiKER trails on the explore map. `clusters` holds the
 * aggregated points, `unclustered-point` the individual trails — a hit on
 * either means we did render coverage here.
 */
export const TRAIL_LAYER_IDS = ['clusters', 'unclustered-point'];

/** moveend fires repeatedly through inertial scroll; wait for the map to settle. */
export const SETTLE_MS = 1_200;

export interface CoverageBounds {
  getWest(): number;
  getSouth(): number;
  getEast(): number;
  getNorth(): number;
}

export interface CoverageMapLike {
  getZoom(): number;
  getCenter(): { lng: number; lat: number };
  getBounds(): CoverageBounds | null | undefined;
  getLayer(id: string): unknown;
  queryRenderedFeatures(options: { layers: string[] }): unknown[];
  on(event: string, handler: () => void): void;
}

/**
 * Reads the current viewport into a ViewportSample, or null if the map can't
 * answer yet (style not loaded, no trail layers in this style).
 *
 * Only queries layers that actually exist — Mapbox throws on an unknown layer
 * id, and this runs on every settled pan, so a style without the trail layers
 * would otherwise break the map rather than silently skip.
 */
export function sampleViewport(
  map: CoverageMapLike,
  layerIds: string[] = TRAIL_LAYER_IDS,
): ViewportSample | null {
  try {
    const presentLayers = layerIds.filter((id) => Boolean(map.getLayer(id)));
    if (presentLayers.length === 0) return null;

    const center = map.getCenter();
    if (!center) return null;

    const features = map.queryRenderedFeatures({ layers: presentLayers }) || [];

    const sample: ViewportSample = {
      lon: center.lng,
      lat: center.lat,
      zoom: map.getZoom(),
      trailFeatureCount: features.length,
    };

    const bounds = map.getBounds();
    if (bounds) {
      sample.bounds = [
        bounds.getWest(),
        bounds.getSouth(),
        bounds.getEast(),
        bounds.getNorth(),
      ];
    }

    return sample;
  } catch (error) {
    console.debug('[MapCoverage] could not sample viewport', error);
    return null;
  }
}

export interface AttachOptions {
  reporter?: CoverageReporter;
  layerIds?: string[];
  settleMs?: number;
  setTimeoutFn?: (handler: () => void, timeout: number) => number;
  clearTimeoutFn?: (handle: number) => void;
}

/**
 * Start reporting empty viewports on this map. Safe to call once per map
 * instance; returns a teardown for tests and for maps that get destroyed.
 */
export function attachMapCoverageReporting(
  map: CoverageMapLike,
  {
    reporter = createCoverageReporter({ send: sendMapCoverageEvent }),
    layerIds = TRAIL_LAYER_IDS,
    settleMs = SETTLE_MS,
    setTimeoutFn = (handler, timeout) => window.setTimeout(handler, timeout),
    clearTimeoutFn = (handle) => window.clearTimeout(handle),
  }: AttachOptions = {},
): () => void {
  let handle: number | null = null;

  const onSettled = () => {
    if (handle !== null) clearTimeoutFn(handle);
    handle = setTimeoutFn(() => {
      handle = null;
      const sample = sampleViewport(map, layerIds);
      if (sample) reporter.report(sample);
    }, settleMs);
  };

  map.on('moveend', onSettled);

  return () => {
    if (handle !== null) clearTimeoutFn(handle);
    handle = null;
  };
}
